import { useTranslation } from 'react-i18next'
import { Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { DashboardSummary, DashboardStatistics } from '../../api'

const TOTAL_STEPS = 5

export type StepState = 'pending' | 'active' | 'complete'

export type StepStatusColor = 'red' | 'amber' | 'green'

export interface MobilisationStepperProps {
    currentStep: number
    completedCount: number
    stepLabels: string[]
    onStepClick: (step: number) => void
    /** When set, step 1–4 circles use same colors as card (red/amber/green by metric) */
    dashboardSummary?: DashboardSummary | null
    dashboardStats?: DashboardStatistics | null
    /** How many steps this mobilisation has: three in Recovery Manager, where the technical checklist and the impact assessment are their own views. */
    totalSteps?: number
}

function stepStatusColor(
    step: number,
    summary: DashboardSummary | null | undefined,
    stats: DashboardStatistics | null | undefined
): StepStatusColor | null {
    if (step === 5) return null
    if (step === 1 && summary != null) {
        const p = summary.percentActivated
        if (p <= 0) return 'red'
        if (p >= 100) return 'green'
        return 'amber'
    }
    if (step === 2 && summary != null) {
        return summary.noMessagesSent <= 0 ? 'red' : 'green'
    }
    if (step === 3 && stats != null) {
        const p = stats.mobPercentComplete
        if (p <= 0) return 'red'
        if (p >= 100) return 'green'
        return 'amber'
    }
    if (step === 4 && stats != null) {
        const p = stats.taskPercentComplete
        if (p <= 0) return 'red'
        if (p >= 100) return 'green'
        return 'amber'
    }
    return null
}

const STEP_ORDER = [1, 2, 3, 4, 5] as const

/** Circle: border color with slightly lighter background (match grey circle style) */
const statusCircleClasses: Record<StepStatusColor, string> = {
    red: 'border-red-500 bg-red-500/20 text-red-700 dark:text-red-400',
    amber: 'border-amber-500 bg-amber-500/20 text-amber-700 dark:text-amber-400',
    green: 'border-green-500 bg-green-500/20 text-green-700 dark:text-green-400',
}

export function MobilisationStepper({
    currentStep,
    completedCount,
    stepLabels,
    onStepClick,
    dashboardSummary,
    dashboardStats,
    totalSteps = TOTAL_STEPS,
}: MobilisationStepperProps) {
    const { t } = useTranslation()
    const hasDashboardData = dashboardSummary != null || dashboardStats != null

    return (
        <div className="rounded-lg border bg-card p-4 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                    <span>{t('recoverRequests.mobilisation.progress', 'Progress')}</span>
                    <span className="tabular-nums text-foreground">
                        {Math.min(completedCount, totalSteps)}/{totalSteps}
                    </span>
                </div>
                <div className="flex flex-1 flex-wrap items-center justify-center gap-1 sm:gap-2">
                    {STEP_ORDER.filter((step) => step <= totalSteps).map((step) => {
                        const label = stepLabels[step - 1]
                        const state: StepState =
                            step < currentStep ? 'complete' : step === currentStep ? 'active' : 'pending'
                        const statusColor = stepStatusColor(step, dashboardSummary, dashboardStats)

                        const useStatusColor = hasDashboardData && statusColor != null
                        const buttonClass =
                            state === 'active' ? 'bg-muted/50 text-foreground' : 'text-muted-foreground'
                        const circleClass = useStatusColor
                            ? statusCircleClasses[statusColor]
                            : state === 'complete'
                              ? 'border-green-500 bg-green-500/20 text-green-700 dark:text-green-400'
                              : state === 'active'
                                ? 'border-muted-foreground/40 bg-muted/50'
                                : 'border-muted-foreground/40 bg-muted/50'

                        return (
                            <button
                                key={step}
                                type="button"
                                onClick={() => onStepClick(step)}
                                className={cn(
                                    'inline-flex items-center gap-2 rounded-md px-2 py-1.5 text-sm font-medium transition-colors hover:bg-muted/80',
                                    buttonClass
                                )}
                            >
                                <span
                                    className={cn(
                                        'flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 text-xs',
                                        circleClass
                                    )}
                                >
                                    {state === 'complete' && !useStatusColor ? (
                                        <Check className="h-4 w-4" />
                                    ) : (
                                        step
                                    )}
                                </span>
                                <span className="hidden sm:inline">{label}</span>
                            </button>
                        )
                    })}
                </div>
            </div>
        </div>
    )
}
